<template>
  <JoszakiPage inner-class="flex flex-col gap-4">
    <BreadCrumbs :links="breadCrumbs" />
    <div ref="header">
      <JoszakiHeading class="text-center mb-4">
        {{ content.heading }}
      </JoszakiHeading>
      <QuickOffer
        @requestTender="onRequestTender({ source: 'quickOffer', text: $event })"
      />
    </div>

    <p class="text-lg">{{ content.lead }}</p>

    <JoszakiLoading
      :active="loading && personList.length < 1"
      :is-full-page="false"
    />
    <div v-if="useVerticalCardMobile" class="grid grid-cols-2 gap-3">
      <template v-if="loading">
        <CardPersonPlaceholder v-for="i in pageSize" :key="i" />
      </template>
      <template v-else>
        <ProfessionalPortraitCard
          v-for="person in personList"
          :key="person.id"
          class="h-full"
          :person="person"
          :profession-name="page.profession.name"
          :highlighted-profession="page.profession"
          :highlighted-city="city"
          :show-city="false"
          @select="onClickPersonCard(person)"
          @callProfessional="onCallProfessional(person)"
          @requestTender="onRequestTender({ person, source: 'personCard' })"
        />
      </template>
    </div>
    <div v-else class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <template v-if="loading">
        <CardPersonPlaceholder v-for="i in pageSize" :key="i" />
      </template>
      <template v-else>
        <CardPerson
          v-for="person in personList"
          :key="person.id"
          class="h-full"
          :highlighted-city="city"
          :highlighted-profession-task="page.professionTask"
          :person="person"
          show-active-indicator
          @select="onClickPersonCard(person)"
          @callProfessional="onCallProfessional(person)"
          @requestTender="onRequestTender({ person, source: 'personCard' })"
        />
      </template>
    </div>

    <PaginationComponent
      v-if="paginationInfo && paginationInfo.last !== 1"
      :pagination-info="paginationInfo"
    />

    <OfferBox
      class="max-lg:hidden"
      :placeholder="$t('quickOffer.placeholder')"
      @requestTender="onRequestTender({ source: 'offerBox', text: $event })"
    />
    <BlockTender
      class="lg:hidden"
      text-input
      @requestTender="onRequestTender({ source: 'banner-block', text: $event })"
    />

    <ProfessionTaskCityContent :page="page" :sections="content.sections" />
  </JoszakiPage>
</template>

<script>
import { ref, useContext } from "@nuxtjs/composition-api";
import ProfessionTaskCityContent from "./ProfessionTaskCityContent.vue";
import {
  buildProfessionTaskCityContent,
  capitalizeFirst,
  cityForms,
  lowerFirst,
  replaceTaskPagePlaceholders,
} from "./professionTaskCityContent";
import { professionTaskPagePath } from "./professionTaskPageOutcome";
import { useFetchProfessionTaskProfessionals } from "./useFetchProfessionTaskProfessionals";
import BreadCrumbs from "~/components/_refactored/BreadCrumbs.vue";
import QuickOffer from "~/components/_refactored/app/boxes/QuickOffer.vue";
import BlockTender from "~/components/_refactored/blocks/BlockTender.vue";
import CardPersonPlaceholder from "~/components/_refactored/card/CardPersonPlaceholder.vue";
import PaginationComponent from "~/components/_refactored/common/PaginationComponent.vue";
import ProfessionalPortraitCard from "~/components/_refactored/indexPage/front-page-modules/ProfessionalPortraitCard.vue";
import CardPerson from "~/components/people/CardPerson.vue";
import OfferBox from "~/components/people/OfferBox.vue";
import { useRequestTender } from "~/composables/requestTender";
import { useNewTender } from "~/composables/tender/useNewTender";
import {
  useTrackFrontendEvent,
  FRONTEND_EVENT_TYPE,
  FRONTEND_EVENT_PLACEMENT,
} from "~/composables/tracking/useTrackFrontendEvent";
import useHashParams from "~/composables/useHashParams";
import { generatePreloadLinks } from "~/helpers/files";
import NunitoVariableLatin from "~/assets/fonts/nunito-variable-latin.woff2";
import NunitoVariableLatinExt from "~/assets/fonts/nunito-variable-latin-ext.woff2";

export default {
  name: "ProfessionTaskCityPage",
  components: {
    BlockTender,
    BreadCrumbs,
    CardPerson,
    CardPersonPlaceholder,
    OfferBox,
    PaginationComponent,
    ProfessionalPortraitCard,
    ProfessionTaskCityContent,
    QuickOffer,
  },
  props: {
    page: {
      type: Object,
      required: true,
    },
    city: {
      type: Object,
      required: true,
    },
    stats: {
      type: Object,
      default: null,
    },
    people: {
      type: Array,
      default: () => [],
    },
    pagination: {
      type: Object,
      default: null,
    },
    pageSize: {
      type: Number,
      required: true,
    },
    siblingTasks: {
      type: Array,
      default: () => [],
    },
  },
  setup(props) {
    const { $sentry } = useContext();
    const newTender = useNewTender();
    newTender.showFloater();

    const fetchProfessionals = useFetchProfessionTaskProfessionals();
    const { trackFrontendEvent } = useTrackFrontendEvent();
    const header = ref(null);
    const personList = ref(props.people);
    const paginationInfo = ref(props.pagination);
    const loading = ref(false);

    const loadPage = async (pageNumber) => {
      loading.value = true;
      try {
        const response = await fetchProfessionals({
          professionTaskSeoName: props.page.professionTask.seoName,
          citySeoName: props.city.seoName,
          page: pageNumber,
          pageSize: props.pageSize,
        });
        personList.value = response.people;
        paginationInfo.value = response.pagination;
      } catch (error) {
        console.error("Failed to load profession task city page", error);
        $sentry.captureException(error);
      }
      loading.value = false;
    };

    useHashParams({ onParamsChange: loadPage, scrollToRef: header });

    return {
      ...useRequestTender(),
      trackFrontendEvent,
      header,
      personList,
      paginationInfo,
      loading,
    };
  },
  head() {
    const nthPage =
      this.paginationInfo?.current > 1
        ? this.$t("seo.professionTaskCityPage.pageNumber", {
            pageNumber: this.paginationInfo.current,
          })
        : "";
    const title = capitalizeFirst(
      `${
        this.replacePlaceholders(this.page.metaTitle) ||
        this.$t("seo.professionTaskCityPage.title", {
          taskName: this.page.professionTask.name,
          cityName: this.city.name,
        })
      } ${nthPage}`.trim()
    );
    const description =
      this.replacePlaceholders(this.page.metaDescription) ||
      this.$t("seo.professionTaskCityPage.description", {
        count: this.paginationInfo?.total ?? 0,
        task: lowerFirst(this.page.professionTask.name),
        inCity: cityForms(this.city).inCity,
      });

    return {
      title,
      meta: [
        { hid: "og:title", property: "og:title", content: title },
        {
          hid: "og:url",
          property: "og:url",
          content: `${this.$helpers.topDomain}${this.$route.fullPath}`,
        },
        { hid: "og:image", property: "og:image", content: "/joszaki-head.png" },
        { hid: "description", name: "description", content: description },
        {
          hid: "og:description",
          property: "og:description",
          content: description,
        },
      ],
      link: [
        {
          rel: "canonical",
          href: `${this.$helpers.topDomain}${this.$route.path}`,
        },
      ].concat(
        generatePreloadLinks([NunitoVariableLatin, NunitoVariableLatinExt])
      ),
    };
  },
  computed: {
    content() {
      return buildProfessionTaskCityContent({
        t: (key, params) => this.$t(key, params),
        formatCurrency: (value) => this.$n(value, "currency"),
        formatRating: (value) => this.$n(value, "rating"),
        page: this.page,
        city: this.city,
        stats: this.stats,
        professionalCount: this.paginationInfo?.total,
        siblingTasks: this.siblingTasks,
      });
    },
    breadCrumbs() {
      const { profession, professionTask } = this.page;
      const links = [];
      if (profession.professionGroup) {
        links.push({
          name: profession.professionGroup.name,
          page: this.localePath({
            name: "professionsLandingPage",
            hash: `#${profession.professionGroup.seoName}`,
          }),
        });
      }
      links.push({
        name: profession.plural,
        page: this.localePath({
          name: "professionPage",
          params: { profession: profession.seoName },
        }),
      });
      links.push({
        name: professionTask.name,
        page: professionTaskPagePath(this.localePath, { slug: this.page.slug }),
      });
      links.push({ name: this.city.name, link: "" });
      return links;
    },
    useVerticalCardMobile() {
      return (
        this.$device.isMobile &&
        this.$ab.isVariant("profession-city-page-vertical-card-mobile", "test")
      );
    },
  },
  mounted() {
    this.$trackers.pageview({
      pagename: this.$trackers.pageNames.PROFESSION_TASK_CITY_PAGE,
      pagenumber: this.paginationInfo?.current,
      professionTaskSeoName: this.page.professionTask.seoName,
      professionSeoName: this.page.profession.seoName,
      citySeoName: this.city.seoName,
      personIds: this.personList.map((person) => person.id),
    });
  },
  beforeDestroy() {
    this.$trackers.pageleave({
      pagename: this.$trackers.pageNames.PROFESSION_TASK_CITY_PAGE,
      pagenumber: this.paginationInfo?.current,
    });
  },
  methods: {
    replacePlaceholders(text) {
      return replaceTaskPagePlaceholders(text, {
        taskName: this.page.professionTask.name,
        cityName: this.city.name,
        professionName: this.page.profession.name,
      });
    },
    profilePath(person) {
      const basePath = this.localePath({
        name: "professionalPage",
        params: { professionalSeoName: person.seoName },
      });
      return `${basePath}#city=${this.city.seoName}&professionTask=${this.page.professionTask.seoName}`;
    },
    onClickPersonCard(person) {
      window.open(this.profilePath(person));
    },
    onCallProfessional(person) {
      this.trackFrontendEvent({
        eventType: FRONTEND_EVENT_TYPE.PHONE_CLICK,
        personId: person.id,
        placement: FRONTEND_EVENT_PLACEMENT.PERSON_CARD,
        metadata: {
          professionTaskSeoName: this.page.professionTask.seoName,
          professionSeoName: this.page.profession.seoName,
          citySeoName: this.city.seoName,
        },
      });
      this.$trackers.trackEvent({
        title: "Clicked On Mate Phone Number",
        data: {
          personId: person.id,
          professionTaskSeoName: this.page.professionTask.seoName,
          professionSeoName: this.page.profession.seoName,
          citySeoName: this.city.seoName,
        },
      });
      window.open(this.profilePath(person));
    },
    onRequestTender({ person, source, text }) {
      this.requestTender({
        person,
        profession: this.page.profession,
        city: this.city,
        source,
        description: text,
      });
    },
  },
};
</script>
